// src/components/charts/Sparkline.tsx
//
// ★★★ WAVE 27 — the smallest chart in the product, and the one with the most ways
// to be dishonest.
//
// ══ WHAT IT IS FOR ══
//
// A KPI tile answers "how many" with a number. A sparkline answers "and which way
// has it been going" in the same glance, without a second screen. `dataviz`'s form
// heuristic puts this exactly where it belongs: the tile's job is a single
// headline, and the shape beside it is context, not a chart a reader interrogates.
//
// ══ ⚠ THE FOUR RULES THIS COMPONENT ENFORCES RATHER THAN TRUSTS ══
//
//  1. **It is `aria-hidden`, and the tile MUST carry the trend in words.** A shape
//     with no axis and no labels is unreadable to a screen reader and to anyone who
//     cannot resolve a 40px glyph. The tile prints "▲ 189% vs the previous 30 days"
//     as text; this decorates that sentence, it never replaces it
//     (`ux-direction.md` §5.3 rule 1 — nothing encodes meaning by colour or shape
//     alone).
//  2. **The baseline is ZERO, never the minimum.** A sparkline scaled to its own
//     min–max turns a 2% wobble into a mountain range. That is the single most
//     common lie told with this form, and it is the one `anti-patterns.md` names
//     as truncated-axis. Every series here is a count, so zero is a real floor and
//     using it costs nothing but honesty.
//  3. **A flat series draws a flat line**, not an empty box — a scope whose count
//     did not move has a shape, and it is a horizontal one.
//  4. **Fewer than two points draws NOTHING.** One point is not a trend, and a
//     single dot on a tile reads as one. The tile renders its number and no shape.
import type { ReactElement } from 'react'

export interface SparklinePoint {
  readonly date: string
  readonly count: number
}

/**
 * @param points in chronological order — this component never sorts them. The
 *   server decides what order a series is in; re-sorting here would be a second
 *   place for that answer to live.
 * @param label what the shape is OF, for the title element. Not rendered visually.
 */
export function Sparkline({
  points,
  label,
}: {
  readonly points: readonly SparklinePoint[]
  readonly label: string
}): ReactElement | null {
  if (points.length < 2) return null

  const width = 120
  const height = 28
  // ⚠ Zero floor — see rule 2. `max` is the only bound taken from the data.
  const max = points.reduce((highest, p) => Math.max(highest, p.count), 0)
  const span = max === 0 ? 1 : max
  const step = points.length === 1 ? 0 : width / (points.length - 1)

  const coords = points.map((p, index) => {
    const x = index * step
    // 2px of headroom top and bottom so a peak is not clipped by the viewBox and
    // a zero still sits on a visible line rather than on the edge.
    const y = height - 2 - (p.count / span) * (height - 4)
    return `${x.toFixed(2)},${y.toFixed(2)}`
  })

  return (
    <svg
      className="pt-sparkline"
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      role="img"
      aria-hidden="true"
      focusable="false"
      preserveAspectRatio="none"
    >
      <title>{label}</title>
      {/*
        ⚠ The area is drawn UNDER the line at low opacity rather than as a second
        colour. §5.3 rule 6 forbids gradients and glows; a flat tint of the same
        series hue is the one fill this form needs, and it makes the zero floor
        visible without a drawn axis.
      */}
      <polygon
        className="pt-sparkline-area"
        points={`0,${height} ${coords.join(' ')} ${width},${height}`}
      />
      <polyline className="pt-sparkline-line" points={coords.join(' ')} />
    </svg>
  )
}
